<template>
  <base-page-layout ref="base-page" :title="alteracaoTitle" class="alteracao-campanha">
    <v-container class="pa-0" fluid>
      <v-row>
        <v-col>
          <title-description
            :title.sync="campaign.title"
            :description.sync="campaign.description"
            :title-error="$v.campaign.title.$error"
            :title-error-message="errorTitle"
            :description-error="$v.campaign.description.$error"
            :description-error-message="errorDescription"
            :status="campaign.status"
          ></title-description>
        </v-col>
      </v-row>

      <v-row>
        <v-col v-if="isOfferSchedulePermissionActive" cols="3" class="d-flex">
          <offer-schedule-card
            :offer-schedule="offerSchedule"
            :has-selected-products="campaign.products && campaign.products.length > 0 && !isOfferSchedule"
            show-products-warning
            :disabled="campaign.products && campaign.products.length > 0 && !isOfferSchedule"
            :block-offers-types="blockOffersTypes"
          />
        </v-col>

        <v-col :cols="isOfferSchedulePermissionActive ? 9 : 12" class="d-flex flex-column">
          <products-configuration
            v-if="!$getFirstValue()"
            :products.sync="campaign.products"
            class="mb-6 fill-height"
            :is-offer-schedule="isOfferSchedule"
            show-offer-schedule-warning
          />

          <segments-configuration
            :segments.sync="campaign.segments"
            :error="getSafe(() => $v.campaign.segments.$error)"
            :is-offer-schedule-personalized="isOfferSchedulePersonalized"
            class="fill-height"
          />
        </v-col>
      </v-row>

      <v-row>
        <v-col class="py-0">
          <push-configuration
            :push-configuration.sync="campaign.message"
            :template-id.sync="campaign.template_id"
            :hasTitleError="getSafe(() => $v.campaign.message.title.$error)"
            :hasTextError="getSafe(() => $v.campaign.message.text.$error)"
            :hasMessageTitleError="getSafe(() => $v.campaign.message.inner_message_title.$error)"
            :hasMessageTextError="getSafe(() => $v.campaign.message.inner_message_text.$error)"
            :hasImageError="getSafe(() => $v.campaign.image_url.$error)"
            :campaign="campaign"
            :show-image.sync="localShowImage"
          />
        </v-col>
      </v-row>

      <redirection
        :tab.sync="tab"
        :offer-schedule-id="campaign.offers_schedule_id"
        :redirect-external.sync="redirectExternal"
        :redirect-internal.sync="redirectInternal"
        :products="campaign.products"
        :selected-flyer.sync="selectedFlyer"
        :validations="$v"
      />

      <v-row>
        <v-col cols="12">
          <measuring-and-recurrency v-model="campaign" :validations="$v" />
        </v-col>
      </v-row>

      <mf-action-buttons :primary-button="primaryButton" :secondary-button="secondaryButton" :extra-button="deleteButton" :cancel-button="cancelButton" />
    </v-container>
  </base-page-layout>
</template>

<script>
import BasePageLayout from '@/components/BasePageLayout.vue'
import { minLength, maxLength, required, minValue } from 'vuelidate/lib/validators'
import { notOnlySpace, isFuture, timeIsFuture, isUrl } from '@/helpers/validators.js'
import { deepDelete } from '@/helpers/deepDelete'
import { characterVariables } from '@/mixins/character-variables'
import { actionButtons } from '../../../views/mixins/action-buttons.js'
import Swal from 'sweetalert2'
import { MUTATION_UPSERT_DRAFT_PUSH_CAMPAIGN, MUTATION_UPSERT_PUSH_CAMPAIGN } from '../../graphql'
import { mapActions, mapGetters } from 'vuex'
import { cloneDeep } from 'lodash'
import Redirection from '../components/Redirection.vue'

export default {
  components: {
    BasePageLayout,
    OfferScheduleCard: () => import('@/modules/campanhas/views/components/edit/OfferScheduleCard.vue'),
    ProductsConfiguration: () => import('@/modules/campanhas/views/components/edit/ProductsConfiguration.vue'),
    PushConfiguration: () => import('@/modules/campanhas/campanhas-aplicativo/views/components/PushConfiguration.vue'),
    SegmentsConfiguration: () => import('@/modules/campanhas/views/components/edit/SegmentsConfiguration.vue'),
    TitleDescription: () => import('@/modules/campanhas/views/components/edit/TitleDescription.vue'),
    MeasuringAndRecurrency: () => import('@/modules/campanhas/views/components/edit/MeasuringAndRecurrency.vue'),
    Redirection,
  },
  mixins: [characterVariables, actionButtons],
  props: {
    campaign: Object,
    offerSchedule: {
      type: Object,
      default: () => ({}),
    },
    isOfferSchedulePermissionActive: {
      type: Boolean,
      default: false,
    },
    isOfferSchedulePersonalized: {
      type: Boolean,
      default: false,
    },
    showImage: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    savingDraft: false,
    redirectPage: 'aplicativo',
    tab: 'internal',

    redirectExternal: '',
    redirectInternal: 'home',
    selectedFlyer: '',
  }),
  computed: {
    ...mapGetters({ quickAction: 'getQuickAction' }),
    modules() {
      return this.$getAllowedModules()
    },
    blockOffersTypes() {
      return [
        'dynamic_rule',
        'dynamic_rule_offer_sales',
        'next_buy_cashback',
        'next_buy_cashback_offer_sales',
        'cross_sell',
        'crosssell',
        'cross_sell_offer_sales',
      ]
    },
    maxTitleLength() {
      return this.lengthCounter(this.campaign.message.title, 48)
    },
    maxTextLength() {
      return this.lengthCounter(this.campaign.message.text, 100)
    },
    maxMessageTitleLength() {
      return this.lengthCounter(this.campaign.message.inner_message_title, 50)
    },
    maxMessageTextLength() {
      return this.lengthCounter(this.campaign.message.inner_message_text, 500)
    },
    storedCampaign() {
      return this.$store.getters.pushCampaign
    },
    errorTitle() {
      if (!this.$v.campaign.title) return ''
      if (this.$v.campaign.title.$error) {
        if (!this.$v.campaign.title.required) return 'Dê um nome para esta campanha.'
        if (!this.$v.campaign.title.minLength) return 'O título possui menos de 2 caracteres.'
        if (!this.$v.campaign.title.notOnlySpace) return 'Não é permitido salvar apenas espaços.'
      }
      return ''
    },
    errorDescription() {
      if (!this.$v.campaign.description) return ''
      if (this.$v.campaign.description.$error) {
        if (!this.$v.campaign.description.required) return 'Informe detalhes para melhor identificação da campanha.'
        if (!this.$v.campaign.description.minLength) return 'A descrição possui menos de 2 caracteres.'
        if (!this.$v.campaign.description.notOnlySpace) return 'Não é permitido salvar apenas espaços.'
      }
      return ''
    },
    alteracaoTitle() {
      if (this.newCampaign) return this.$t('campanhas.nova_campanha')
      return this.$t('campanhas.editar_campanha')
    },
    newCampaign() {
      let path = this.$router.currentRoute.fullPath
      return path.includes('nova') && true
    },
    isOfferSchedule() {
      return !!this.campaign.offers_schedule_id
    },
    getAction() {
      let action = {}

      switch (this.tab) {
        case 'external':
          action = {
            type: 'EXTERNAL',
            link: this.redirectExternal,
            reference_id: null,
          }
          break
        case 'internal':
          action = {
            type: 'INTERNAL',
            link: this.redirectInternal === 'home' ? '' : this.redirectInternal,
            reference_id: this.redirectInternal === 'FlyersScreen' ? this.selectedFlyer : null,
          }
          break
        case 'offer':
          action = {
            type: 'INTERNAL',
            link: 'SalesCampaignScreen',
            reference_id: null,
          }
          break
      }

      return action
    },
    localShowImage: {
      get() {
        return this.showImage
      },
      set(value) {
        this.$emit('update:showImage', value)
      },
    },
  },
  watch: {
    'campaign.message.innerMessage'() {
      this.campaign.message.inner_message_title = ''
      this.campaign.message.inner_message_text = ''
    },
  },
  created() {
    if (this.campaign.products) {
      this.campaign.products = this.campaign.products.filter((product) => Boolean(product))
    }
  },
  mounted() {
    this.prebuildAutoCampaign()
    if (this.campaign?.action?.type) {
      const { link, type, reference_id } = this.campaign.action
      if (type === 'EXTERNAL_LINK') {
        this.tab = 'external'
        this.redirectExternal = link
      } else if (type === 'VIEW_CAMPAIGN_SALES') this.tab = 'offer'
      else {
        this.tab = 'internal'
        this.redirectInternal = link || 'home'
        this.selectedFlyer = link === 'FlyersScreen' ? reference_id : null
      }
    }
  },
  methods: {
    ...mapActions(['unsetQuickAction']),

    groupProducts(allProducts) {
      if (!allProducts.length) return []

      const groups = {}
      const products = []

      allProducts.forEach((item) => {
        if (item.isGroup) {
          if (!groups[item.nameGroup]) {
            groups[item.nameGroup] = {
              isGroup: true,
              name: item.nameGroup,
              products: [],
            }
          }

          const groupProduct = {
            ean: item.ean,
            eans: item.eans,
            name: item.name,
            sku: item.sku,
            _id: item._id,
            product: item.product,
          }
          groups[item.nameGroup].products.push(groupProduct)
        } else {
          products.push(item)
        }
      })

      const formattedProducts = Object.values(groups).concat(products)
      return formattedProducts
    },

    async save(isDraft) {
      this.savingDraft = isDraft
      this.$v.$reset()
      this.$v.$touch()

      if (this.$v.$error) {
        this.$snackbar({ message: 'Preencha todos os campos corretamente', snackbarColor: 'error' })
        this.$nextTick(() => this.$vuetify.goTo(document.querySelector('.error--text'), { offset: 200 }))
        return
      }

      const campaign = cloneDeep(this.campaign)

      if (campaign.frequency.type === 'ONETIME') {
        delete campaign.frequency.interval
      }

      if (campaign.message.innerMessage === false) {
        campaign.message.inner_message_title = ''
        campaign.message.inner_message_text = ''
      }
      deepDelete(campaign, ['__typename', 'status', 'innerMessage'])
      campaign.segments = campaign.segments.map(({ _id }) => _id)
      campaign.products = campaign.products.map(({ sku }) => sku)

      if (!this.showImage) {
        campaign.image_url = ''
      }

      campaign.action = this.getAction

      const mutation = isDraft ? MUTATION_UPSERT_DRAFT_PUSH_CAMPAIGN : MUTATION_UPSERT_PUSH_CAMPAIGN
      await this.$apollo.mutate({
        mutation: mutation,
        variables: {
          data: campaign,
        },
        context: {
          uri: this.$microservicesUrls['campaigns'],
        },
      })

      Swal.fire({
        text: 'Sua campanha foi salva',
        icon: 'success',
        showConfirmButton: false,
        timer: 1000,
        customClass: {
          content: 'content-class',
        },
      })

      this.$router.push('/campanhas/aplicativo').catch(() => {})
    },

    async prebuildAutoCampaign() {
      if (!this.getIsQuickAction() || !this.quickAction?.segmentation) {
        return
      }

      this.campaign.segments.push(this.quickAction.segmentation.segment)
      this.campaign.title = this.quickAction.segmentation.title
      this.campaign.description = this.quickAction.segmentation.description

      this.unsetQuickAction()
    },
  },
  validations() {
    if (this.savingDraft) {
      return {
        campaign: {
          title: {
            required,
            minLength: minLength(2),
            notOnlySpace,
          },
          description: {
            notOnlySpace,
          },
        },
      }
    }
    return {
      ...(this.tab === 'external'
        ? {
            redirectExternal: {
              required,
              notOnlySpace,
              isUrl,
            },
          }
        : {}),
      campaign: {
        title: {
          required,
          minLength: minLength(2),
          notOnlySpace,
        },
        description: {
          required,
          minLength: minLength(2),
          notOnlySpace,
        },
        segments: {
          required,
          minLength: minLength(1),
        },
        initial_date: {
          required,
        },
        final_date: {
          required,
        },
        frequency: {
          interval: {
            minValue: minValue(1),
          },
        },
        ...(this.showImage
          ? {
              image_url: {
                required,
                notOnlySpace,
              },
            }
          : {}),
        message: {
          send_date: {
            required,
            isFuture,
          },
          send_time: {
            required,
            timeIsFuture: function (value) {
              return timeIsFuture(this.campaign.message.send_date, value)
            },
          },
          title: {
            required,
            maxLength: maxLength(this.maxTitleLength),
            notOnlySpace,
          },
          text: {
            maxLength: maxLength(this.maxTextLength),
            notOnlySpace,
          },
          inner_message_title: {
            maxLength: maxLength(this.maxMessageTitleLength),
            notOnlySpace,
          },
          inner_message_text: {
            maxLength: maxLength(this.maxMessageTextLength),
            notOnlySpace,
          },
        },
      },
    }
  },
}
</script>
